import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon } from '@heroicons/react/24/outline';

interface PageHeroProps {
    title: string;
    subtitle?: string;
    icon?: React.ReactNode;
    accentColor?: 'blue' | 'purple' | 'green' | 'orange' | 'pink' | 'cyan';
    children?: React.ReactNode;
}

const accentColors = {
    blue: {
        tube: 'linear-gradient(90deg, #1e3a8a 0%, #1e40af 15%, #2563eb 35%, #3b82f6 50%, #2563eb 65%, #1e40af 85%, #1e3a8a 100%)',
        glow: 'rgba(59, 130, 246, 0.15)',
        text: 'from-slate-900 via-blue-900 to-slate-900',
        badge: 'bg-blue-500',
        shimmer: 'rgba(59, 130, 246, 0.15)',
    },
    purple: {
        tube: 'linear-gradient(90deg, #581c87 0%, #6b21a8 15%, #7c3aed 35%, #8b5cf6 50%, #7c3aed 65%, #6b21a8 85%, #581c87 100%)',
        glow: 'rgba(139, 92, 246, 0.15)',
        text: 'from-slate-900 via-purple-900 to-slate-900',
        badge: 'bg-purple-500',
        shimmer: 'rgba(139, 92, 246, 0.15)',
    },
    green: {
        tube: 'linear-gradient(90deg, #14532d 0%, #166534 15%, #16a34a 35%, #22c55e 50%, #16a34a 65%, #166534 85%, #14532d 100%)',
        glow: 'rgba(34, 197, 94, 0.15)',
        text: 'from-slate-900 via-green-900 to-slate-900',
        badge: 'bg-green-500',
        shimmer: 'rgba(34, 197, 94, 0.15)',
    },
    orange: {
        tube: 'linear-gradient(90deg, #7c2d12 0%, #9a3412 15%, #ea580c 35%, #f97316 50%, #ea580c 65%, #9a3412 85%, #7c2d12 100%)',
        glow: 'rgba(249, 115, 22, 0.15)',
        text: 'from-slate-900 via-orange-900 to-slate-900',
        badge: 'bg-orange-500',
        shimmer: 'rgba(249, 115, 22, 0.15)',
    },
    pink: {
        tube: 'linear-gradient(90deg, #831843 0%, #9d174d 15%, #db2777 35%, #ec4899 50%, #db2777 65%, #9d174d 85%, #831843 100%)',
        glow: 'rgba(236, 72, 153, 0.15)',
        text: 'from-slate-900 via-pink-900 to-slate-900',
        badge: 'bg-pink-500',
        shimmer: 'rgba(236, 72, 153, 0.15)',
    },
    cyan: {
        tube: 'linear-gradient(90deg, #164e63 0%, #155e75 15%, #0891b2 35%, #06b6d4 50%, #0891b2 65%, #155e75 85%, #164e63 100%)',
        glow: 'rgba(6, 182, 212, 0.15)',
        text: 'from-slate-900 via-cyan-900 to-slate-900',
        badge: 'bg-cyan-500',
        shimmer: 'rgba(6, 182, 212, 0.15)',
    },
};

const PageHero: React.FC<PageHeroProps> = ({
    title,
    subtitle,
    icon,
    accentColor = 'blue',
    children,
}) => {
    const colors = accentColors[accentColor];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-8 relative"
        >
            {/* Enhanced Hero Card with Paper Roll Effect */}
            <div
                className="relative w-full overflow-hidden rounded-3xl hero-shimmer-card"
                style={{
                    background: 'linear-gradient(135deg, #fafbfc 0%, #f0f4f8 50%, #f5f7fa 100%)',
                }}
            >
                {/* Enhanced shimmer sweep effect */}
                <div
                    className="absolute inset-0 rounded-3xl pointer-events-none hero-shimmer-border"
                    style={{
                        background: `linear-gradient(90deg, transparent 0%, transparent 35%, ${colors.shimmer} 50%, transparent 65%, transparent 100%)`,
                        backgroundSize: '200% 100%',
                    }}
                />

                {/* Dual ambient glow */}
                <div
                    className="absolute inset-0 rounded-3xl pointer-events-none"
                    style={{
                        background: `radial-gradient(ellipse at 0% 50%, ${colors.glow} 0%, transparent 60%), radial-gradient(ellipse at 100% 50%, ${colors.glow} 0%, transparent 60%)`,
                    }}
                />

                {/* CSS Keyframes */}
                <style>{`
          .hero-shimmer-border {
            animation: shimmerSweep 5s ease-in-out infinite;
          }
          @keyframes shimmerSweep {
            0%, 100% {
              background-position: -100% 0%;
            }
            50% {
              background-position: 200% 0%;
            }
          }
          .hero-shimmer-card {
            box-shadow:
              0 4px 6px -1px rgba(0, 0, 0, 0.04),
              0 10px 20px -3px rgba(0, 0, 0, 0.06),
              0 20px 30px -5px ${colors.glow},
              inset 0 2px 0 rgba(255, 255, 255, 0.7),
              inset 0 -1px 0 rgba(0, 0, 0, 0.03);
            border: 1px solid rgba(59, 130, 246, 0.12);
          }
        `}</style>

                {/* Enhanced floating particles */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
                    {[...Array(6)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="absolute rounded-full"
                            style={{
                                width: `${4 + i * 2}px`,
                                height: `${4 + i * 2}px`,
                                left: `${15 + i * 15}%`,
                                top: `${25 + (i % 3) * 25}%`,
                                filter: 'blur(1px)',
                                background: `linear-gradient(135deg, ${colors.glow}, ${colors.glow})`,
                            }}
                            animate={{
                                y: [0, -15, 0],
                                x: [0, i % 2 === 0 ? 5 : -5, 0],
                                opacity: [0.15, 0.4, 0.15],
                                scale: [1, 1.1, 1],
                            }}
                            transition={{
                                duration: 5 + i * 0.5,
                                repeat: Infinity,
                                delay: i * 0.7,
                                ease: "easeInOut",
                            }}
                        />
                    ))}
                </div>

                <div className="flex items-stretch">

                    {/* Left Side - Enhanced 3D Cylinder Tube */}
                    <motion.div
                        className="relative w-20 lg:w-24 flex-shrink-0 flex items-center justify-center"
                        initial={{ rotateY: -15 }}
                        animate={{ rotateY: 0 }}
                        transition={{ duration: 0.8, type: 'spring' }}
                        style={{
                            background: colors.tube,
                            boxShadow: 'inset -12px 0 24px rgba(0,0,0,0.35), inset 6px 0 12px rgba(255,255,255,0.25), 0 0 0 1px rgba(0,0,0,0.1)',
                            transformStyle: 'preserve-3d',
                        }}
                    >
                        {/* Enhanced tube shine effect */}
                        <motion.div
                            className="absolute inset-0 opacity-50"
                            animate={{
                                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                            }}
                            transition={{
                                duration: 8,
                                repeat: Infinity,
                                ease: 'linear',
                            }}
                            style={{
                                background: 'linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.3) 40%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.3) 60%, transparent 75%)',
                                backgroundSize: '200% 100%',
                            }}
                        />

                        {/* Tube end cap top - enhanced 3D */}
                        <div
                            className="absolute top-0 left-0 right-0 h-3"
                            style={{
                                background: colors.tube,
                                borderRadius: '0 0 50% 50% / 0 0 100% 100%',
                                boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)',
                            }}
                        />

                        {/* Tube end cap bottom - enhanced 3D */}
                        <div
                            className="absolute bottom-0 left-0 right-0 h-3"
                            style={{
                                background: colors.tube,
                                borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
                                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3), 0 -2px 4px rgba(0,0,0,0.2)',
                            }}
                        />

                        {/* Tube center logo with animation */}
                        <motion.div
                            className="relative z-10 w-10 h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center"
                            animate={{
                                rotate: [0, 5, -5, 0],
                                scale: [1, 1.05, 1],
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: 'easeInOut',
                            }}
                            style={{
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.1) 100%)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.4), inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.2)',
                                border: '2px solid rgba(255,255,255,0.2)',
                            }}
                        >
                            {icon || <SparklesIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
                        </motion.div>

                        {/* Tube vertical highlights */}
                        <div
                            className="absolute top-3 bottom-3 left-1/4 w-px opacity-30"
                            style={{
                                background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)',
                            }}
                        />
                        <div
                            className="absolute top-3 bottom-3 right-1/4 w-px opacity-20"
                            style={{
                                background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
                            }}
                        />
                    </motion.div>

                    {/* Right Side - Enhanced 3D Unrolled Paper */}
                    <div
                        className="flex-1 relative overflow-hidden"
                        style={{
                            background: 'linear-gradient(135deg, #ffffff 0%, #fefefe 30%, #fcfcfd 60%, #f8fafc 100%)',
                            boxShadow: `
                inset 6px 0 24px rgba(0,0,0,0.14),
                inset 0 3px 6px rgba(0,0,0,0.03),
                0 6px 12px rgba(0,0,0,0.05),
                0 2px 4px rgba(0,0,0,0.03)
              `,
                            transformOrigin: 'left center',
                            transformStyle: 'preserve-3d',
                            borderRadius: '0 24px 24px 0',
                        }}
                    >
                        {/* Enhanced paper texture overlay */}
                        <div
                            className="absolute inset-0 opacity-[0.05] pointer-events-none"
                            style={{
                                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.95' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                            }}
                        />

                        {/* Left edge curl - Enhanced 3D depth effect */}
                        <div
                            className="absolute top-0 left-0 bottom-0 w-10"
                            style={{
                                background: 'linear-gradient(90deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.12) 20%, rgba(0,0,0,0.06) 40%, rgba(0,0,0,0.02) 70%, transparent 100%)',
                            }}
                        />

                        {/* Paper curl highlight */}
                        <div
                            className="absolute top-0 left-0 bottom-0 w-1"
                            style={{
                                background: 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.4) 100%)',
                            }}
                        />

                        {/* Top edge highlight for 3D */}
                        <div
                            className="absolute top-0 left-0 right-0 h-1"
                            style={{
                                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.9) 15%, rgba(255,255,255,0.9) 85%, transparent 100%)',
                            }}
                        />

                        {/* Content */}
                        <div className="relative z-10 px-8 lg:px-12 py-8 lg:py-10">
                            <div className="flex flex-col items-start">

                                {/* Main Title with enhanced animation */}
                                <motion.h1
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3, duration: 0.7, type: 'spring', bounce: 0.3 }}
                                    className={`text-3xl lg:text-4xl font-extrabold bg-gradient-to-r ${colors.text} bg-clip-text text-transparent mb-2`}
                                >
                                    {title}
                                </motion.h1>

                                {subtitle && (
                                    <motion.p
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.5, duration: 0.7 }}
                                        className="text-sm lg:text-base text-slate-600 font-medium flex items-center gap-2"
                                    >
                                        <span className={`inline-block w-2 h-2 ${colors.badge} rounded-full animate-pulse`}></span>
                                        {subtitle}
                                    </motion.p>
                                )}

                                {/* Additional content slot */}
                                {children && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.7, duration: 0.7 }}
                                        className="mt-4"
                                    >
                                        {children}
                                    </motion.div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom accent strip */}
                <div
                    className="h-1"
                    style={{
                        background: `linear-gradient(90deg, ${colors.tube.split(',')[2]} 0%, ${colors.tube.split(',')[3]} 20%, transparent 20%)`,
                    }}
                />
            </div>

            {/* Subtle glow */}
            <div
                className="absolute -bottom-4 left-0 w-20 h-6 rounded-full blur-2xl pointer-events-none"
                style={{ background: colors.glow }}
            />
        </motion.div>
    );
};

export default PageHero;
